import {
  AxiomaW1Alarm,
  AxiomaW1,
  AxiomaW1Telemetry,
  AxiomaW1TelemetryField,
  Installable,
  Uninstallable
} from '../types'
import i18next from 'i18next'
import { formatValue, roundValue } from './string'
import {
  AxiomaW1AlarmCode,
  AxiomaW1BatteryLevel,
  AxiomaW1NominalDiameter,
  AxiomaW1SignalStrength
} from '../enums'
import { MISCELLANEOUS_W1_ALARM_CODES } from '../constants/axiomaW1'

type TOptions = {
  ns: string
}

const t = (value: string, { ns = 'axioma' }: TOptions = { ns: 'axioma' }) =>
  i18next.t(value, { ns })

export function translateAxiomaW1TelemetryField(field: AxiomaW1TelemetryField, unit = false) {
  let value = ''
  switch (field) {
    case 'volume':
      value = t('totalVolume', { ns: 'axioma' })
      break
    case 'flow':
      value = t('latestFlow', { ns: 'axioma' })
      break
    case 'temperature':
      value = t('waterTemperature', { ns: 'axioma' })
      break
    case 'signal_rssi':
      value = t('signalRssi', { ns: 'axioma' })
      break
    case 'signal_rsrq':
      value = t('signalRsrq', { ns: 'axioma' })
      break
    case 'signal_ecl':
      value = t('signalEcl', { ns: 'axioma' })
      break
    case 'signal_band':
      value = t('signalBand', { ns: 'axioma' })
      break
    case 'modem_tx_time':
      value = t('modemTxTime', { ns: 'axioma' })
      break
    case 'modem_rx_time':
      value = t('modemRxTime', { ns: 'axioma' })
      break
    case 'modem_active_duration':
      value = t('modemActiveDuration', { ns: 'axioma' })
      break
    case 'modem_credits':
      value = t('modemCredits', { ns: 'axioma' })
      break
    case 'modem_status':
      value = t('modemStatus', { ns: 'axioma' })
      break
    case 'battery_voltage':
      value = t('batteryVoltage', { ns: 'axioma' })
      break
    case 'battery_percentage':
      value = t('batteryPercentage', { ns: 'axioma' })
      break
  }

  if (unit) {
    const unit = getAxiomaW1TelemetryFieldUnit(field)
    if (unit) {
      value += ` (${unit})`
    }
  }

  return value
}

export function formatAxiomaW1TelemetryValue(
  field: AxiomaW1TelemetryField,
  value: number | null,
  withUnit = false
) {
  let decimals = 2
  switch (field) {
    case 'modem_credits':
    case 'modem_status':
    case 'battery_percentage':
      decimals = 0
      break
  }

  let formattedValue = formatValue(value, decimals)

  if (withUnit) {
    const unit = getAxiomaW1TelemetryFieldUnit(field)
    if (unit) {
      formattedValue = `${formattedValue} ${unit}`
    }
  }

  return formattedValue
}

export function roundAxiomaW1TelemetryValue(field: AxiomaW1TelemetryField, value: number) {
  return roundValue(value, 2)
}

export function getAxiomaW1TelemetryFieldUnit(field: AxiomaW1TelemetryField) {
  switch (field) {
    case 'volume':
      return 'm³'
    case 'flow':
      return 'm³/h'
    case 'temperature':
      return '°C'
    case 'signal_rssi':
    case 'signal_rsrq':
    case 'signal_ecl':
      return 'dBm'
    case 'battery_voltage':
      return 'V'
    case 'battery_percentage':
      return '%'
    default:
      return null
  }
}

export function formatNominalDiameter(DN: AxiomaW1NominalDiameter, unit = true) {
  return unit ? `${DN} mm` : `${DN}`
}

export function translateAxiomaW1AlarmCode(code: AxiomaW1AlarmCode): string {
  switch (code) {
    case AxiomaW1AlarmCode.LEAKAGE:
      return t('leakage', { ns: 'axioma' })
    case AxiomaW1AlarmCode.BURST:
      return t('burst', { ns: 'axioma' })
    case AxiomaW1AlarmCode.FREEZE_ALERT:
      return t('freeze', { ns: 'axioma' })
    case AxiomaW1AlarmCode.EMPTY_PIPE:
      return t('emptyPipe', { ns: 'axioma' })
    case AxiomaW1AlarmCode.REVERSE_FLOW:
      return t('reverseFlow', { ns: 'axioma' })
    case AxiomaW1AlarmCode.OVERFLOW:
      return t('overflow', { ns: 'axioma' })
    case AxiomaW1AlarmCode.NO_CONSUMPTION:
      return t('noConsumption', { ns: 'axioma' })
    case AxiomaW1AlarmCode.DAMAGE_TO_METER_HOUSING:
      return t('tamper', { ns: 'axioma' })
    case AxiomaW1AlarmCode.LOW_BATTERY:
      return t('lowBattery', { ns: 'axioma' })
    case AxiomaW1AlarmCode.COMMUNICATION_TEMPORARILY_BLOCKED:
      return t('communicationTemporarilyBlocked', { ns: 'axioma' })
    case AxiomaW1AlarmCode.HARDWARE_FAILURE_DETECTED:
    case AxiomaW1AlarmCode.CALCULATOR_HARDWARE_FAILURE:
      return t('hardwareFailure', { ns: 'axioma' })
    case AxiomaW1AlarmCode.SOFTWARE_FAILURE_DETECTED:
      return t('softwareFailure', { ns: 'axioma' })
    default:
      return code
  }
}

export function getAxiomaW1AlarmSeverity(code: AxiomaW1AlarmCode) {
  switch (code) {
    case AxiomaW1AlarmCode.BURST:
    case AxiomaW1AlarmCode.LEAKAGE:
    case AxiomaW1AlarmCode.DAMAGE_TO_METER_HOUSING:
    case AxiomaW1AlarmCode.COMMUNICATION_TEMPORARILY_BLOCKED:
    case AxiomaW1AlarmCode.HARDWARE_FAILURE_DETECTED:
    case AxiomaW1AlarmCode.SOFTWARE_FAILURE_DETECTED:
      return 'error'
    case AxiomaW1AlarmCode.NO_CONSUMPTION:
    case AxiomaW1AlarmCode.EMPTY_PIPE:
    case AxiomaW1AlarmCode.REVERSE_FLOW:
    case AxiomaW1AlarmCode.FREEZE_ALERT:
    case AxiomaW1AlarmCode.LOW_BATTERY:
    case AxiomaW1AlarmCode.OVERFLOW:
    default:
      return 'warning'
  }
}

export function calculateSignalStrength(telemetry: AxiomaW1Telemetry): AxiomaW1SignalStrength {
  if (telemetry.signal_rssi >= -70) {
    return AxiomaW1SignalStrength.EXCELLENT
  } else if (telemetry.signal_rssi >= -85) {
    return AxiomaW1SignalStrength.GOOD
  } else if (telemetry.signal_rssi >= -100) {
    return AxiomaW1SignalStrength.FAIR
  } else if (telemetry.signal_rssi >= -110) {
    return AxiomaW1SignalStrength.POOR
  } else {
    return AxiomaW1SignalStrength.NO_SIGNAL
  }
}

export function calculateSignalPercentage(telemetry: AxiomaW1Telemetry): number {
  const rssi = telemetry.signal_rssi
  if (rssi >= -70) {
    return 100
  } else if (rssi >= -85) {
    return Math.round(((rssi + 85) / 15) * 100)
  } else if (rssi >= -100) {
    return Math.round(((rssi + 100) / 15) * 100)
  } else if (rssi >= -110) {
    return Math.round(((rssi + 110) / 10) * 100)
  } else {
    return 0
  }
}

export function translateSignalStrength(signalStrength: AxiomaW1SignalStrength) {
  switch (signalStrength) {
    case AxiomaW1SignalStrength.EXCELLENT:
      return t('excellentSignal', { ns: 'axioma' })
    case AxiomaW1SignalStrength.GOOD:
      return t('goodSignal', { ns: 'axioma' })
    case AxiomaW1SignalStrength.FAIR:
      return t('fairSignal', { ns: 'axioma' })
    case AxiomaW1SignalStrength.POOR:
      return t('poorSignal', { ns: 'axioma' })
    case AxiomaW1SignalStrength.NO_SIGNAL:
      return t('noSignal', { ns: 'axioma' })
  }
}

export function calculateBatteryLevel(telemetry: AxiomaW1Telemetry): AxiomaW1BatteryLevel {
  if (telemetry.battery_voltage >= 3.8) {
    return AxiomaW1BatteryLevel.FULL
  } else if (telemetry.battery_voltage >= 3.6) {
    return AxiomaW1BatteryLevel.HIGH
  } else if (telemetry.battery_voltage >= 3.4) {
    return AxiomaW1BatteryLevel.MEDIUM
  } else if (telemetry.battery_voltage >= 3.2) {
    return AxiomaW1BatteryLevel.LOW
  } else if (telemetry.battery_voltage >= 3.0) {
    return AxiomaW1BatteryLevel.CRITICAL
  } else {
    return AxiomaW1BatteryLevel.EMPTY
  }
}

export function calculateBatteryPercentage(telemetry: AxiomaW1Telemetry): number {
  const voltageRange = 3.8 - 3.0
  const voltage = telemetry.battery_voltage - 3.0
  return Math.max(0, Math.min(100, (voltage / voltageRange) * 100))
}

export function isMiscAlarmCode(code: AxiomaW1AlarmCode): boolean {
  return MISCELLANEOUS_W1_ALARM_CODES.includes(code)
}

export function isMiscAlarm(alarm: AxiomaW1Alarm): boolean {
  return isMiscAlarmCode(alarm.errorCode)
}

export function translateBatteryLevel(level: AxiomaW1BatteryLevel) {
  switch (level) {
    case AxiomaW1BatteryLevel.FULL:
      return t('fullBattery', { ns: 'axioma' })
    case AxiomaW1BatteryLevel.HIGH:
      return t('highBattery', { ns: 'axioma' })
    case AxiomaW1BatteryLevel.MEDIUM:
      return t('mediumBattery', { ns: 'axioma' })
    case AxiomaW1BatteryLevel.LOW:
      return t('lowBattery', { ns: 'axioma' })
    case AxiomaW1BatteryLevel.CRITICAL:
      return t('criticalBattery', { ns: 'axioma' })
    case AxiomaW1BatteryLevel.EMPTY:
      return t('emptyBattery', { ns: 'axioma' })
  }
}

export function isInstallable(axiomaW1: AxiomaW1): axiomaW1 is Installable<AxiomaW1> {
  return axiomaW1?.asset?.project !== undefined && axiomaW1?.asset?.project === null
}

export function isUninstallable(axiomaW1: AxiomaW1): axiomaW1 is Uninstallable<AxiomaW1> {
  return axiomaW1?.asset?.project !== undefined && axiomaW1?.asset?.project !== null
}
